import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { MdClear, MdHelp } from 'react-icons/md';
import { BiTrashAlt } from 'react-icons/bi';

import styles from './style/Dashboard.module.scss';
import Modal from '../Utilities/Modal';
import Alert from '../Utilities/Alert';
import Button from '../Utilities/Button';
import TextField from '../Utilities/TextField';
import Dropdown from '../Utilities/DropdownDynamic';

import { createPlanPackageNew, selectPlans } from '../../store/slice/manager/plans';
import { ClipLoader } from 'react-spinners';
import Datepicker from '../Utilities/Datepicker/Datepicker';
import { toChineseNumber } from '../../utils';

// 方案種類：對應後端 package 的 type。只有「席位方案(stored_value)」允許填多筆面額。
const PLAN_TYPE_OPTIONS = [
  { label: '訂閱', value: 'group_by_plan' },
  { label: '席位方案', value: 'stored_value' },
];

// 服務對象：對應後端 software_service_plans.type（DB 預設 center）。center=核心服務 / company=企業服務。
const SERVICE_TARGET_OPTIONS = [
  { label: '核心服務', value: 'center' },
  { label: '企業服務', value: 'company' },
];

// 付費週期：對應後端 software_service_plans.pay_type。
// month → 輸入月費，寫入 monthly_additional_fee；year → 輸入年費，寫入 rental_fee。
const PAY_TYPE_OPTIONS = [
  { label: '月費', value: 'month' },
  { label: '年費', value: 'year' },
];

const ModalCreatePlan = ({ createPlan, setCreatePlan, setStatus, setToastValue, restart, serviceId: propServiceId, defaultType }) => {
  const dispatch = useDispatch();
  const params = useParams();
  // 一般路由用 params.id；以 modal 嵌入時改用傳入的 serviceId。
  const serviceId = propServiceId != null ? String(propServiceId) : params.id;
  const { newPlanLoading } = useSelector(selectPlans);
  const modalRef = useRef(null);

  //trial plan state
  const [error, setError] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [planTitle, setPlanTitle] = useState('');
  const [planDate, setPlanDate] = useState({ startDay: '', endDay: '' });
  const [isTipShow, setIsTipShow] = useState(-1);

  const [isTrialPlanChecked, setIsTrialPlanChecked] = useState(false);
  const [maxUsers, setMaxUsers] = useState('');
  const [validityDays, setValidityDays] = useState('');

  const [planList, setPlanList] = useState([
    {
      name: '',
      users_count: 0,
      validity_days: 0,
      rental_fee: 0,
      discount: 100,
      span: 0,
    },
  ]);

  // 方案種類；席位方案時改用 feeList（一個面額 = 一筆，只差金額）。
  // 寫死為開啟分頁的型別（在訂閱分頁按新增就是訂閱、席位分頁就是席位），不可於 modal 內切換。
  const planType = defaultType || 'group_by_plan';
  const planTypeLabel = PLAN_TYPE_OPTIONS.find((o) => o.value === planType)?.label;
  const [feeList, setFeeList] = useState([{ name: '', rental_fee: '' }]);

  // 服務對象（center / company）；整個方案組合的所有 plan 共用同一個對象。
  const [serviceTarget, setServiceTarget] = useState('center');

  // 付費週期（month / year）；整包共用。切換後輸入欄標籤與送出欄位一併切換。
  const [payType, setPayType] = useState('month');
  const isYearPay = payType === 'year';
  const feeLabel = isYearPay ? '年費' : '月費';
  const feeUnitHint = isYearPay ? '每人/年 (元)' : '每人/月 (元)';

  const handleFeeChange = (index, key, value) => {
    setFeeList((list) => {
      const next = [...list];
      next[index] = { ...next[index], [key]: value };
      return next;
    });
  };

  const handleAddFee = () => setFeeList((list) => [...list, { name: '', rental_fee: '' }]);

  const handleDeleteFee = (index) => setFeeList((list) => list.filter((_, i) => i !== index));

  const checkFeeListError = () =>
    feeList.some((fee) => !fee.name || fee.name === '' || !fee.rental_fee || Number(fee.rental_fee) <= 0);

  const handleCreatePlanResult = (result) => {
    if (result.payload?.msg === 'success') {
      restart();
      setStatus('create');
      setError(false);
      setCreatePlan(false);
      setPlanTitle('');
      setIsTrialPlanChecked(false);
      setMaxUsers(null);
      setValidityDays(null);
      return;
    }
    if (result.meta?.requestStatus === 'rejected') {
      setSubmitError(result.payload?.msg || '新增方案組合失敗');
    }
  };

  // 儲值金送出：type=stored_value，每個面額展開成一筆 plan（只帶 name + rental_fee）。
  const handleStoredValueSubmit = () => {
    if (planTitle !== '' && planDate.startDay !== '' && !checkFeeListError()) {
      const data = {
        service: serviceId,
        type: 'stored_value',
        name: planTitle,
        publish_date: format(planDate.startDay, 'yyyy-MM-dd'),
        plans: feeList.map((fee) => ({ name: fee.name, rental_fee: Number(fee.rental_fee), type: serviceTarget })),
      };
      if (planDate.endDay !== '') {
        data.publish_end_date = format(planDate.endDay, 'yyyy-MM-dd');
      }
      dispatch(createPlanPackageNew(data)).then(handleCreatePlanResult);
    } else {
      setError(true);
    }
  };

  const handleStartDay = (date, startDay) => {
    const day = new Date(date);
    return format(day.setDate(day.getDate() - (startDay ? 1 : 0)), 'yyyy-MM-dd');
  };

  const handlePlanListChange = (index, type, value) => {
    setPlanList((e) => {
      e[index][type] = type === 'name' ? value : +value;
      return [...e];
    });
  };

  const handleDeleteCard = (index) => {
    setPlanList((e) => {
      return [...e.filter((_, cardIndex) => cardIndex !== index)];
    });
  };

  const checkPlanListError = () => {
    let error = false;
    planList.forEach((e) => {
      if (error) return;
      if (!e.name || e.name === '') error = true;
      if (!e.users_count || e.users_count <= 0) error = true;
      if (!e.validity_days || e.validity_days <= 0) error = true;
      if (!e.rental_fee || e.rental_fee <= 0) error = true;
      if ((!e.discount && e.rental_fee !== 0) || e.discount < 0 || e.discount > 100) error = true;
      if (!e.span || e.span <= 0) error = true;
    });
    return error;
  };

  return (
    <Modal isOpen={createPlan} size='lg' height='calc(100vh - 120px)'>
      {createPortal(
        <Alert
          mainTitle='新增方案組合失敗'
          subTitle={submitError}
          isOpen={!!submitError}
          onConfirm={() => setSubmitError('')}
          onClose={() => setSubmitError('')}
          rightButtonText='確認'
        />,
        document.body
      )}
      <div className={styles.ModalContainer} ref={modalRef}>
        {newPlanLoading ? (
          <div className={styles.bigLoading}>
            <ClipLoader size={60} />
          </div>
        ) : (
          <>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>新增{planTypeLabel}方案組合</div>
              <div className={styles.modalClose}>
                <Button
                  type={'tertiary'}
                  icon={<MdClear />}
                  color={'#5856D6'}
                  onClick={() => {
                    setCreatePlan(false);
                    setPlanTitle('');
                    setSubmitError('');

                    setIsTrialPlanChecked(false);
                    setMaxUsers(null);
                    setValidityDays(null);
                  }}
                />
              </div>
            </div>
            <div className={`${styles.modalBody}  ${styles.createNewPlan}`}>
              <div className={styles.planTitleContainer}>
                {/* 方案種類寫死為開啟分頁的型別，僅顯示、不可切換。 */}
                <div className={styles.planTitle}>方案種類</div>
                <div className={styles.planTypeSelect}>
                  <div style={{ display: 'inline-block', padding: '10px 14px', fontWeight: 600, color: '#333' }}>{planTypeLabel}</div>
                </div>

                <div className={styles.planTitle}>服務對象</div>
                <div className={styles.planTypeSelect}>
                  <Dropdown
                    caption='label'
                    value={SERVICE_TARGET_OPTIONS.find((o) => o.value === serviceTarget)?.label}
                    listItems={SERVICE_TARGET_OPTIONS}
                    onChange={(item) => setServiceTarget(item.value)}
                    wide={true}
                  />
                </div>

                <div className={styles.planTitle}>付費週期</div>
                <div className={styles.planTypeSelect}>
                  <Dropdown
                    caption='label'
                    value={PAY_TYPE_OPTIONS.find((o) => o.value === payType)?.label}
                    listItems={PAY_TYPE_OPTIONS}
                    onChange={(item) => setPayType(item.value)}
                    wide={true}
                  />
                </div>

                <div className={styles.planTitle}>
                  方案組合名稱
                  {!planTitle && error && <div className={styles.titleErr}>方案組合名稱不可為空</div>}
                </div>
                <TextField
                  placeholder={'請輸入'}
                  width='200px'
                  error={!planTitle && error}
                  onChange={(e) => {
                    setPlanTitle(e);
                  }}
                  value={planTitle || ''}
                />

                <div className={styles.planPeriod}>
                  方案時間
                  <Datepicker
                    selectDate={planDate.startDay}
                    setSelectDate={(e) => setPlanDate({ ...planDate, startDay: e })}
                    startYear={`${handleStartDay(new Date()).split('-')[0]}`}
                    disableEvents={[
                      {
                        start: `${handleStartDay(new Date())}`,
                        end: `${planDate.endDay !== '' ? planDate.endDay : '2100-12-31'}`,
                      },
                    ]}
                    error={error && !planDate.startDay}
                  />
                  至
                  {/* 結束日選填：留空＝永久上架（送出時 planDate.endDay === '' 就不帶 publish_end_date）。 */}
                  {/* 下限鎖在開始日（未選開始日則鎖今天），確保結束不早於開始，對齊 PlanManagement 的雙日期選法。 */}
                  <Datepicker
                    selectDate={planDate.endDay}
                    setSelectDate={(e) => setPlanDate({ ...planDate, endDay: e })}
                    startYear={`${handleStartDay(new Date()).split('-')[0]}`}
                    disableEvents={[
                      {
                        start: `${planDate.startDay !== '' ? handleStartDay(planDate.startDay, true) : handleStartDay(new Date())}`,
                        end: `2100-12-31`,
                      },
                    ]}
                    placeholderText='永久'
                  />
                </div>
              </div>
              {/* start試用方案 */}

              {/* 試用方案end */}
              {planType !== 'stored_value' && (
              <div className={styles.planLayout}>
                {planList.map((planData, index) => (
                  <div key={index} className={styles.planCard}>
                    <div className={styles.planCardTop}>
                      <div className={styles.planCardTitle}>方案{toChineseNumber(index + 1)}</div>
                      <Button
                        type='pure'
                        icon={<BiTrashAlt />}
                        color={'#ff766e'}
                        disabled={index === 0 && planList.length === 1 ? true : false}
                        onClick={() => handleDeleteCard(index)}
                      />
                    </div>
                    <div className={styles.planCardMid}>
                      <div className={styles.planCardSelectTitleLeft}>方案名稱</div>
                      <TextField
                        placeholder={'請輸入'}
                        width='200px'
                        error={!planData.name && error}
                        onChange={(e) => {
                          handlePlanListChange(index, 'name', e);
                        }}
                        value={planData.name || ''}
                      />
                      <div className={styles.planCardSelectTitleMid}>基本人數</div>
                      <TextField
                        placeholder='請輸入'
                        width='170px'
                        numeric={true}
                        value={planData.users_count || ''}
                        onChange={(e) => {
                          handlePlanListChange(index, 'users_count', e);
                        }}
                        error={(!planData.users_count || planData.users_count <= 0) && error}
                        min={1}
                      />
                      <div className={styles.planCardSelectTitleRight}>綁約天數</div>
                      <TextField
                        placeholder='請輸入'
                        width='150px'
                        numeric={true}
                        value={planData?.validity_days || ''}
                        onChange={(e) => {
                          handlePlanListChange(0, 'validity_days', e);
                        }}
                        error={(!planData.validity_days || planData.validity_days <= 0) && error}
                        min={1}
                      />
                    </div>
                    <div className={styles.planCardMid} style={{ margin: '-5px 0' }}>
                      <div className={styles.planCardSelectTitleLeft} />
                      <div className={styles.errorMessage} style={{ width: '200px' }}>
                        {!planData.name && error && '未輸入方案名稱'}
                      </div>
                      <div className={styles.planCardSelectTitleMid} />
                      <div className={styles.errorMessage} style={{ width: '170px' }}>
                        {(!planData.users_count || planData.users_count <= 0) && error && '未輸入基本人數'}
                      </div>
                      <div className={styles.planCardSelectTitleRight} />
                      <div className={styles.errorMessage} style={{ width: '150px' }}>
                        {(!planData.validity_days || planData.validity_days <= 0) && error && '未輸入綁約天數'}
                      </div>
                    </div>
                    <div className={styles.planCardBottom}>
                      <div className={styles.planCardSelectTitleLeft}>
                        {feeLabel}
                        <span style={{ fontSize: '10px', marginLeft: '3px' }}>{feeUnitHint}</span>
                      </div>
                      <TextField
                        placeholder='請輸入'
                        width='200px'
                        numeric={true}
                        value={planData.rental_fee || ''}
                        onChange={(e) => {
                          handlePlanListChange(index, 'rental_fee', e);
                        }}
                        error={(!planData.rental_fee || planData.rental_fee <= 0) && error}
                        min={1}
                      />
                      <div className={styles.planCardSelectTitleMid}>
                        折扣(%)
                        <div className={styles.helpBtnOuter}>
                          <Button
                            type='pure'
                            icon={<MdHelp />}
                            onMouseEnter={() => setIsTipShow(index)}
                            onMouseLeave={() => setIsTipShow(-1)}
                            color={'#B4B4B4'}
                          />
                          {Number(isTipShow) === Number(index) && (
                            <div className={styles.tips}>
                              <span>設定 100% ，完全無折扣，</span>
                              <span>設定 0%，整筆折扣掉，付款金額為0。</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <TextField
                        placeholder='請輸入'
                        width='170px'
                        numeric={true}
                        isDisabled
                        value={planData.discount || ''}
                        onChange={(e) => {
                          handlePlanListChange(0, 'discount', e);
                        }}
                        error={(!planData.discount || planData.discount > 100 || planData.discount < 0) && error}
                        min={0}
                        max={100}
                      />
                      <div className={styles.planCardSelectTitleRight}>增加帳號數級距</div>
                      <TextField
                        placeholder='請輸入'
                        width='150px'
                        numeric={true}
                        value={planData.span || ''}
                        onChange={(e) => {
                          handlePlanListChange(index, 'span', e);
                        }}
                        error={(!planData.span || planData.span <= 0) && error}
                        min={1}
                      />
                    </div>
                    <div className={styles.planCardBottom} style={{ margin: '-5px 0' }}>
                      <div className={styles.planCardSelectTitleLeft} />
                      <div className={styles.errorMessage} style={{ width: '200px' }}>
                        {(!planData.rental_fee || planData.rental_fee <= 0) && error && '未輸入費用'}
                      </div>
                      <div className={styles.planCardSelectTitleMid} />
                      <div className={styles.errorMessage} style={{ width: '170px' }}>
                        {(!planData.discount || planData.discount > 100 || planData.discount < 0) &&
                          error &&
                          `${planData.discount > 100 || planData.discount < 0 ? '請輸入 0 ~ 100 之間' : '未輸入折扣'}`}
                      </div>
                      <div className={styles.planCardSelectTitleRight} />
                      <div className={styles.errorMessage} style={{ width: '150px' }}>
                        {(!planData.span || planList[index].span <= 0) && error && '未輸入增加帳號數級距'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              )}

              {planType === 'stored_value' && (
                <div className={styles.planLayout}>
                  {feeList.map((fee, index) => (
                    <div key={index} className={styles.planCard}>
                      <div className={styles.planCardTop}>
                        <div className={styles.planCardTitle}>面額{toChineseNumber(index + 1)}</div>
                        <Button
                          type='pure'
                          icon={<BiTrashAlt />}
                          color={'#ff766e'}
                          disabled={index === 0 && feeList.length === 1}
                          onClick={() => handleDeleteFee(index)}
                        />
                      </div>
                      <div className={styles.planCardMid}>
                        <div className={styles.planCardSelectTitleLeft}>方案名稱</div>
                        <TextField
                          placeholder={'請輸入'}
                          width='200px'
                          error={!fee.name && error}
                          onChange={(e) => handleFeeChange(index, 'name', e)}
                          value={fee.name || ''}
                        />
                        <div className={styles.planCardSelectTitleMid}>
                          費用<span style={{ fontSize: '10px', marginLeft: '3px' }}>(元)</span>
                        </div>
                        <TextField
                          placeholder='請輸入'
                          width='170px'
                          numeric={true}
                          value={fee.rental_fee || ''}
                          onChange={(e) => handleFeeChange(index, 'rental_fee', e)}
                          error={(!fee.rental_fee || Number(fee.rental_fee) <= 0) && error}
                          min={1}
                        />
                      </div>
                      <div className={styles.planCardMid} style={{ margin: '-5px 0' }}>
                        <div className={styles.planCardSelectTitleLeft} />
                        <div className={styles.errorMessage} style={{ width: '200px' }}>
                          {!fee.name && error && '未輸入方案名稱'}
                        </div>
                        <div className={styles.planCardSelectTitleMid} />
                        <div className={styles.errorMessage} style={{ width: '170px' }}>
                          {(!fee.rental_fee || Number(fee.rental_fee) <= 0) && error && '未輸入費用'}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div style={{ marginTop: '12px' }}>
                    <Button type='tertiary' title={'新增面額'} onClick={handleAddFee} />
                  </div>
                </div>
              )}
            </div>
            <div className={styles.modalFooter} style={{ backgroundColor: '#ffffff', zIndex: 3 }}>
              <div className={styles.errorImply} style={{ display: 'none' }}>
                <Button
                  type='tertiary'
                  title={'新增方案'}
                  onClick={() =>
                    setPlanList([
                      ...planList,
                      {
                        name: '',
                        users_count: 0,
                        validity_days: 0,
                        rental_fee: 0,
                        discount: 0,
                        span: 0,
                      },
                    ])
                  }
                />
              </div>
              <Button
                type='tertiary'
                title={'完成'}
                onClick={() => {
                  if (planType === 'stored_value') {
                    handleStoredValueSubmit();
                    return;
                  }
                  if (
                    planTitle !== '' &&
                    planDate.startDay !== '' &&
                    (!isTrialPlanChecked || (isTrialPlanChecked && maxUsers > 0 && validityDays > 0)) &&
                    !checkPlanListError()
                  ) {
                    let data = {
                      service: serviceId,
                      type: 'group_by_plan',
                      name: planTitle,
                      publish_date: format(planDate.startDay, 'yyyy-MM-dd'),
                      plans: [],
                    };
                    if (planDate.endDay !== '') {
                      data.publish_end_date = format(planDate.endDay, 'yyyy-MM-dd');
                    }
                    planList.forEach((e, index) => {
                      const planPayload = {
                        plan: ['keystone.core.basic', 'keystone.core.pro'][index],
                        name: e.name,
                        type: serviceTarget,
                        validity_days: e.validity_days,
                        users_count_upper_limit: e.users_count,
                        additional_people_per_unit: e.span,
                        pay_type: payType,
                        discount: 100 - e.discount,
                      };
                      // 年費寫 rental_fee；月費寫 monthly_additional_fee（不再用月費 ×12 當年費）。
                      if (payType === 'year') {
                        planPayload.rental_fee = e.rental_fee;
                      } else {
                        planPayload.monthly_additional_fee = e.rental_fee;
                      }
                      data.plans.push(planPayload);
                    });
                    dispatch(createPlanPackageNew(data)).then(handleCreatePlanResult);
                  } else {
                    setError(true);
                  }
                }}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ModalCreatePlan;
